import type { ComponentsDefinition } from '@eneco/cms';
import { cmsComponents, LogoOxxio } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

/**
 * The CMS-agnostic registry for this container: the shared one plus what only this
 * container mounts.
 *
 * Applications are registered per container on the Sitecore lane too, in
 * `sitecoreComponents.ts`, and for the same reason: they cannot live in the shared
 * registry. `lib:cms-components` may not depend on `app:next` (see `depConstraints`
 * in `eslint.config.mjs`), and every container passes that one map, so a flow put
 * there would also be pulled into the containers that never render it.
 *
 * The cookie wall is one of those applications. It sits in the page's `jss-meta`
 * placeholder, which `CmsLayout` renders on this lane too, and reads its content
 * through `@eneco/cms`, so the same component serves both lanes. The chat sits there too; its CMS-lane entry
 * is `ChatInner`, which reads its content through `@eneco/cms`.
 *
 * The registration flow is registered under both its rendering names as its CMS-lane entry
 * (`RegistrationFlowInner`), which picks the variant from the rendering's name.
 *
 * `AffiliateCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `AdvancedCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `Unsubscribe` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * The logo is Oxxio's own, as `sitecoreComponents.ts` registers Oxxio's `ImageLink` as `Logo`: the shared
 * one is Eneco's.
 *
 * The energy flow reads its content through `@eneco/cms` too, under the same name as
 * in `sitecoreComponents.ts`. So does each flow that shares its steps, such as the
 * urgent energy and contract extension flows, once the steps of its own read their
 * content that way: only then is it added here, as mounting it on this lane earlier
 * would fail at those steps rather than render.
 */
const components: ComponentsDefinition = {
  ...cmsComponents,
  AdvancedCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/AdvancedCalculationTool')),
  AffiliateCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/CalculationTool')),
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat').then(mod => mod.ChatInner)),
  'contract-extension-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/ContractExtensionFlowOxxio')),
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  CustomerReview: dynamic(() =>
    import('@apps/customerreview-shared-multilabel/CustomerReview').then(mod => mod.CustomerReviewInner),
  ),
  'energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/EnergyFlowOxxio')),
  Logo: LogoOxxio,
  RegistrationAccountCreate: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  RegistrationAccountReset: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  Unsubscribe: dynamic(() => import('@apps/unsubscribe-shared-multilabel/Unsubscribe')),
  'urgent-energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/UrgentEnergyFlowOxxio')),
};

export default components;
